import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Live alerts for the Advisor Desk. Polls a tiny events feed every 5s and
// fires `onEvents(newest-first array)` the moment a customer approves,
// replies, asks for a callback, or a text bounces.

let audioCtx = null;
export function unlockAudio() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
  } catch { /* no audio on this device */ }
}

export function chime() {
  if (!audioCtx) return;
  const t = audioCtx.currentTime;
  [[880, 0], [1320, 0.14]].forEach(([freq, delay]) => {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t + delay);
    gain.gain.exponentialRampToValueAtTime(0.3, t + delay + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.5);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(t + delay);
    osc.stop(t + delay + 0.55);
  });
}

const MUTE_KEY = "advisor_alert_muted";
const POLL_MS = 5000;

export function useDeskAlerts(onEvents) {
  const [recent, setRecent] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMutedState] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());
  const mutedRef = useRef(muted);
  const cbRef = useRef(onEvents);
  cbRef.current = onEvents;

  const setMuted = (v) => {
    mutedRef.current = v;
    setMutedState(v);
    localStorage.setItem(MUTE_KEY, v ? "1" : "0");
  };

  useEffect(() => {
    document.addEventListener("pointerdown", unlockAudio, { once: true });
    let cancelled = false;
    const tick = async () => {
      try {
        const first = !sinceRef.current;
        const params = first ? { limit: 15 } : { since: sinceRef.current };
        const r = await api.get("/advisor/events", { params });
        if (cancelled) return;
        const evs = (r.data?.events || []).filter((e) => !seenRef.current.has(e.id));
        evs.forEach((e) => seenRef.current.add(e.id));
        sinceRef.current = r.data?.server_time || new Date().toISOString();
        if (first) { setRecent(evs); return; }
        if (!evs.length) return;
        setRecent((prev) => [...evs, ...prev].slice(0, 25));
        setUnread((n) => n + evs.length);
        const wantsChime = cbRef.current?.(evs);
        if (wantsChime !== false && !mutedRef.current) chime();
      } catch { /* transient — next tick retries */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return { recent, unread, markSeen: () => setUnread(0), muted, setMuted };
}
